import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Swal from 'sweetalert2';
import { errorMessages } from './messagesUtils';

/**
 * HTML要素をPDFに変換して保存する
 * @param {HTMLElement} element - PDF化するHTML要素
 * @param {string} fileName - 保存するPDFのファイル名
 * @returns {Promise<void>} - PDF生成と保存の処理を完了するPromise
 */
export const generatePDF = async (element, fileName) => {
  try {
    let hasError = false;

    if (!element) {
      return;
    }

    // ローディング表示
    const loadingDiv = document.createElement('div');
    loadingDiv.style.position = 'fixed';
    loadingDiv.style.top = '0';
    loadingDiv.style.left = '0';
    loadingDiv.style.width = '100%';
    loadingDiv.style.height = '100%';
    loadingDiv.style.backgroundColor = 'rgb(255, 255, 255)';
    loadingDiv.style.display = 'flex';
    loadingDiv.style.justifyContent = 'center';
    loadingDiv.style.alignItems = 'center';
    loadingDiv.style.zIndex = '9999';
    loadingDiv.innerHTML = '<div style="background: white; padding: 20px; border-radius: 5px; box-shadow: 0 0 10px rgba(0,0,0,0.1);">PDF生成中...</div>';
    document.body.appendChild(loadingDiv);

    // A4サイズの設定（ミリメートル単位）
    const pageWidth = 210;
    const pageHeight = 297;
    
    // PDFインスタンスを作成
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    const container = document.createElement('div');
    container.style.width = '794px';
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    
    // 元の要素をクローンしてコンテナに追加
    const clone = element.cloneNode(true);
    container.appendChild(clone);
    document.body.appendChild(container);
    
    // HTML要素をキャンバスに変換（固定パラメーターを使用）
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      allowTaint: true,
      width: 794
    });
    
    // キャンバスの幅と高さを取得
    const contentWidth = canvas.width;
    const contentHeight = canvas.height;

    // ブレイク位置と要素の高さを格納する配列
    let breakData = [{ position: 0, height: 0 }];

    // クローン要素内でページ分割のためのマーカーを取得
    const cloneBreakElements = clone.querySelectorAll('[data-page-break="true"]');
    const containerRect = container.getBoundingClientRect();


    // クローン内の要素の位置を使って計算
    if (cloneBreakElements.length > 0) {
      for (let i = 0; i < cloneBreakElements.length; i++) {
        const elem = cloneBreakElements[i];
        const elemRect = elem.getBoundingClientRect();
        
        // クローン内での相対位置計算（パディングの影響を受けた正確な位置）
        const relativeTop = elemRect.top - containerRect.top;
        
        // スケーリング
        const scaleFactor = canvas.height / containerRect.height;
        const scaledTop = relativeTop * scaleFactor;
        
        breakData.push({ 
          position: Math.round(scaledTop),
          height: Math.round(elemRect.height * scaleFactor)
        });
      }
    }

    // コンテンツの最後を追加
    breakData.push({ position: contentHeight, height: 0 });

    // 以下、既存の処理を維持
    const MAX_PAGE_HEIGHT = 2200;
    let finalBreakPositions = [0];
    let currentPageHeight = 0;

    for (let i = 1; i < breakData.length; i++) {
      // 現在のブレイクポイントまでの追加コンテンツ高さを計算
      const sectionHeight = breakData[i].position - breakData[i-1].position;
      
      // この追加コンテンツを現在のページに含めた場合の合計高さを計算
      const totalHeight = currentPageHeight + sectionHeight;

      // 合計高さが最大高さを超えるか、最後のブレイクポイントの場合
      if (totalHeight > MAX_PAGE_HEIGHT) {
        // 一つ前のブレイクポイントを採用
        finalBreakPositions.push(breakData[i-1].position);
        // 新しいページの高さをリセット
        currentPageHeight = sectionHeight;
      } else {
        // まだページに収まるので、高さを累積
        currentPageHeight = totalHeight;
      }
    }

    // 重複を排除してソート
    finalBreakPositions = [...new Set(finalBreakPositions)].sort((a, b) => a - b);

    // 各ページを処理
    for (let i = 0; i < finalBreakPositions.length; i++) {
      // 2ページ目以降は新しいページを追加
      if (i > 0) {
        pdf.addPage();
      }
      
      // このページの開始Y座標と高さを計算
      const startY = finalBreakPositions[i];
            
      // 最後のページかどうかを確認
      const isLastPage = i === finalBreakPositions.length - 1;
      
      // 最後のページの場合は、contentHeightまでの高さを使用
      const pageContentHeight = isLastPage 
        ? contentHeight - startY 
        : finalBreakPositions[i + 1] - startY;

      // 描画用に整数変換
      const drawStartY = Math.floor(startY);
      const drawHeight = Math.ceil(pageContentHeight);
      
      // ページの内容が極端に少ない場合はスキップ
      if (pageContentHeight < 10) {
        continue;
      }
      
      try {
        // キャンバス境界のチェック
        if (drawStartY >= canvas.height) {
          continue;
        }
        
        // 範囲が画像の範囲を超えないよう調整
        const adjustedHeight = Math.min(drawHeight, canvas.height - drawStartY);
        
        // 一時的なキャンバスを作成
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = contentWidth;
        tempCanvas.height = adjustedHeight;
        const tempContext = tempCanvas.getContext('2d');
        
        // 元のキャンバスから該当部分を切り出して描画
        tempContext.drawImage(
          canvas,
          0, drawStartY, contentWidth, adjustedHeight,
          0, 0, contentWidth, adjustedHeight
        );
        
        // ページをPDFに追加
        const imgData = tempCanvas.toDataURL('image/png', 1.0);
        
        // アスペクト比を維持してPDFに追加
        const imgHeight = pageWidth * (adjustedHeight / contentWidth);
        
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.7);
        pdf.line(5.3, 6, pageWidth - 5.3, 6);

        pdf.addImage(
          imgData, 
          'JPG',
          0, 6, 
          pageWidth, 
          imgHeight,
          undefined,
          'FAST'
        );
      } catch (err) {
          Swal.fire({ icon: 'error',
            html: errorMessages.pdf.generate_failed,
            customClass: {
              htmlContainer: 'large-text'
            }
          });
          hasError = true;
          break;
      }
    }
    
    // PDFを保存
    if (!hasError) {
      pdf.save(`${fileName}.pdf`);
    }
    
    // 処理後にコンテナを削除
    document.body.removeChild(container);
    
    // ローディング表示を削除
    document.body.removeChild(loadingDiv);
  } catch (error) {
    // エラー時もローディング表示を削除
    const loadingDiv = document.querySelector('div[style*="PDF生成中"]')?.parentNode;
    if (loadingDiv) {
      document.body.removeChild(loadingDiv);
    }

    Swal.fire({ icon: 'error',
      html: errorMessages.pdf.generate_failed,
      customClass: {
        htmlContainer: 'large-text'
      }
    });
  }
};